import { useCallback, useMemo, useRef, useState, useContext } from 'react';
import { throttle } from 'lodash-es';
import { useAppSelector } from './useAppSelector';
import { fetchSuburbSuggestionsFromRex } from 'Api/Rex/rexApi';
import { fetchSuburbSuggestions as fetchRpDataSuburbSuggestions } from 'Api/RpData/rpDataApi';
import { fetchSuburbSuggestionsFromGoogle } from 'utils/fetchSuburbSuggestionsFromGoogle';
import { AustralianState, AustralianStateNumericMap } from '@urbanx/agx-ui-components';
import type { SuburbSuggestions } from '@urbanx/agx-ui-components';
import { AuthContext } from '@urbanx/agx.auth.client';

const useSuburbSuggestions = (stateToSearch?: string) => {
  const [loading, setLoading] = useState(false);
  const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbSuggestions[]>([]);
  const activeRequest = useRef<AbortController | undefined>(undefined);
  const { getAuthToken } = useContext(AuthContext);
  const selectedForm = useAppSelector((state) => state.form.selectedForm);
  const targetState = stateToSearch ?? selectedForm?.state;

  const fetchSuggestions = useCallback(
    async (searchQuery?: string) => {
      if (!searchQuery || searchQuery.length < 3) {
        setSuburbSuggestions([]);
        return;
      }

      if (!targetState) return;

      setLoading(true);

      const controller = new AbortController();
      activeRequest.current?.abort();
      activeRequest.current = controller;

      try {
        const authToken = await getAuthToken();
        if (!authToken) return;

        try {
          const result = await fetchSuburbSuggestionsFromRex(
            authToken,
            searchQuery,
            targetState,
            controller.signal
          );

          if (controller.signal.aborted) return;

          if (result && result.length > 0) {
            setSuburbSuggestions(
              result.map((suggestion) => ({
                state: suggestion.stateOrRegion as AustralianState,
                postcode: suggestion.postCode,
                suggestionLine1: suggestion.suburbOrTown,
                suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
              }))
            );
          }
        } catch (rexError) {
          if (controller.signal.aborted) return;

          console.log('Rex API failed, falling back to RPData API:', rexError);

          try {
            // If REX API fails, falling back to I (RPData API)
            const result = await fetchRpDataSuburbSuggestions(
              authToken,
              searchQuery,
              targetState,
              controller.signal
            );

            if (controller.signal.aborted) return;

            if (result?.suburbSuggestions) {
              setSuburbSuggestions(
                result.suburbSuggestions.map((suggestion) => ({
                  state:
                    AustralianState[
                      AustralianStateNumericMap[suggestion.state] as keyof typeof AustralianState
                    ],
                  postcode: suggestion.postcode,
                  suggestionLine1: suggestion.suggestionLine1,
                  suggestionLine2: suggestion.suggestionLine2,
                }))
              );
            }
          } catch (rpDataerror) {
            if (controller.signal.aborted) return;

            console.log('RPData API failed, falling back to Google Places API:', rpDataerror);

            try {
              // If both (REX & RPData APIs) fail, falling back to  (Google Places API)
              const result = await fetchSuburbSuggestionsFromGoogle(searchQuery, targetState);

              if (controller.signal.aborted) return;

              if (result && result.length > 0) {
                setSuburbSuggestions(
                  result
                    .map((suggestion) => ({
                      state: suggestion.stateOrRegion as AustralianState,
                      postcode: suggestion.postCode,
                      suggestionLine1: suggestion.suburbOrTown,
                      suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
                    }))
                    .filter((suggestion) => suggestion.postcode) //Some suburbs don't have postcodes so filtering here
                );
              }
            } catch (googlePlacesApiError) {
              console.log('Google Places API failed', googlePlacesApiError);
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setSuburbSuggestions([]);
      } finally {
        if (activeRequest.current === controller) setLoading(false);
      }
    },
    [getAuthToken, targetState]
  );
  const getSuburbSuggestions = useMemo(() => throttle(fetchSuggestions, 100), [fetchSuggestions]);

  return { suburbSuggestions, loading, getSuburbSuggestions };
};

export default useSuburbSuggestions;
